import { Component } from 'react';
import type { PropsWithChildren, ReactNode } from 'react';

import { decorateErrorForReporting } from '@change-corgi/core/errorReporter/common';

type Props = PropsWithChildren<{
	/**
	 * for Sentry, this is used to generate tags
	 *
	 * Special keys:
	 * - userAgent: will be used by the sentry reporter to add userAgent-specific tags
	 */
	readonly context?: Record<string, unknown>;
	/**
	 * for Sentry, this is the extras object
	 */
	readonly params?: Record<string, unknown>;
}>;

type State = Readonly<{
	hasError: boolean;
	error?: unknown;
}>;

/**
 * catches errors, decorates them with additional context and rethrows them to be handled by the error boundary
 *
 * this component acts as an error boundary that intercepts errors from its children,
 * applies the `decorate` function to add additional properties/context,
 * and rethrows the decorated error so it propagates to the nearest parent error boundary
 */
export class ErrorDecorator extends Component<Props, State> {
	constructor(props: Props) {
		super(props);
		this.state = { hasError: false };
	}

	static getDerivedStateFromError(error: unknown): State {
		return { hasError: true, error };
	}

	render(): ReactNode {
		const { hasError, error } = this.state;

		if (hasError) {
			const { context, params } = this.props;
			// rethrowing from render propagates to the parent error boundary
			// (a component cannot catch errors from its own render method)
			throw decorateErrorForReporting(error as Error, { context, params });
		}

		const { children } = this.props;

		return children;
	}
}
